import * as z from "zod/v4";
import { queryOptions } from "@tanstack/react-query";
import { api } from "../http";

export const participantSchema = z.object({
  id: z.uuid(),
  meeting_bot_id: z.uuid(),
  full_name: z.string(),
  speaker_id: z.string(),
  created_at: z.iso.datetime(),
  voice_type: z.enum(["Real", "Fake"]).nullable(),
  analytics: z
    .object({
      ai_content_detection: z.object({
        ai: z.number().nullable(),
        human: z.number().nullable(),
      }),
      pii: z.object({
        count: z.number(),
        by_type: z.record(z.string(), z.number()),
      }),
      voice_authentication: z.object({
        real_ratio: z.number().nullable(),
        fake_ratio: z.number().nullable(),
      }),
      risk_score: z.object({
        total: z.number().nullable(),
        ai_component: z.number().nullable(),
        pii_component: z.number().nullable(),
        vexon_component: z.number().nullable(),
        data_sources: z.array(z.string()),
      }),
      speech_duration_ms: z.number(),
    })
    .nullable(),
});

export type ParticipantDetails = z.infer<typeof participantSchema>;

export function participantDetailQueryOptions(participantId: string) {
  return queryOptions({
    queryKey: ["participants", participantId],
    queryFn: () =>
      api.processing.raw
        .get(`api/v1/participants/${participantId}`, {
          searchParams: {
            include_analytics: true, // it's always needed in here anyways and this simplifies the return type
          },
        })
        .json<ParticipantDetails>(),
  });
}

export type ParticipantAnalysisTimelinePiiItem = {
  utterance_id: string;
  start_time_ms: number;
  entity_type: string;
  entity_text: string;
  confidence: number;
  severity: "CRITICAL" | "CONFIDENTIAL" | "STANDARD";
};
export type ParticipantAnalysisTimelineAiDetectionItem = {
  utterance_id: string;
  start_time_ms: number;
  end_time_ms: number;
  label: string;
  score: number;
};
export type ParticipantAnalysisTimelineVexonItem = {
  utterance_id: string;
  start_time_ms: number;
  end_time_ms: number;
  authenticity_result: string;
  fake_prob: number;
  real_prob: number;
  confidence: number;
};
export type ParticipantAnalysisTimeline = {
  pii: Array<ParticipantAnalysisTimelinePiiItem>;
  ai_content_detection: Array<ParticipantAnalysisTimelineAiDetectionItem>;
  voice_authentication: Array<ParticipantAnalysisTimelineVexonItem>;
};

export function participantAnalysisTimelineQueryOptions(participantId: string) {
  return queryOptions({
    queryKey: ["participants", participantId, "analysis-timeline"],
    queryFn: () =>
      api.processing.raw
        .get(`api/v1/participants/${participantId}/analysis-timeline`)
        .json<ParticipantAnalysisTimeline>(),
  });
}
